import { useParams } from 'react-router-dom';
import { Container, Title, Text, Paper, Group, Badge, Alert, Stack } from '@mantine/core';
import { IconLock, IconAlertCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { shareLinksApi } from '@/services/share-links.api';
import SimulatorPanel from '@/features/editor/components/SimulatorPanel';

export default function SharedFlowPage() {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['sharedFlow', token],
    queryFn: () => shareLinksApi.getSharedFlow(token!),
  });

  if (isLoading) {
    return (
      <Container size="lg" pt="xl">
        <Text>Loading shared flow...</Text>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" pt="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {(error as Error).message || 'Failed to load shared flow'}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container size="lg" pt="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Not Found" color="yellow">
          This shared flow could not be found or has been revoked.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="100%" style={{ height: '100vh', padding: 0 }}>
      <Paper shadow="sm" p="md" mb="md" style={{ borderRadius: 0, borderBottom: '1px solid #e0e0e0' }}>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Group gap="sm">
                <Title order={2}>{data.flow.name}</Title>
                <Badge leftSection={<IconLock size={12} />} color="blue" variant="light">
                  Shared link
                </Badge>
              </Group>
              {data.flow.description && (
                <Text size="sm" c="dimmed" mt="xs">{data.flow.description}</Text>
              )}
            </div>
          </Group>
          <Text size="xs" c="dimmed">
            Run this flow to work through the call. Your answers are not kept after
            you finish — anything that needs to be recorded is sent onward by the flow.
          </Text>
        </Stack>
      </Paper>

      <div style={{ height: 'calc(100vh - 140px)' }}>
        <SimulatorPanel
          flowDSL={data.version.dsl}
          mode="live"
          shareToken={token}
          isFullscreen={true}
          autoStart
        />
      </div>
    </Container>
  );
}
